<script lang="ts">
  import BannerDisplay from "@shared/svelte/Components/Banner/BannerDisplay.svelte";
  import TrackingContext from "@shared/svelte/Components/Tracking/TrackingContext.svelte";
  import {
    BannerEntity,
    BannerAdEntity,
    KevelBannerAdEntity,
  } from "@shared/services/event_tracking/entities";
  import { getCloudinaryUrl } from "@shared/services/cloudinary_image_url_service";
  import { cloudinaryParams } from "@shared/services/discovery_image_dimensions";

  import type { Banner } from "@shared/types/Banner";

  export let banners: Banner[];
  export let mobileMode: boolean;
  export let pageName: string | null = null;

  let banner = banners[0];

  const getImageParams = (bannerWidth: number, slotIdString: string) => {
    if (bannerWidth === 3200) {
      return slotIdString === "desktop"
        ? cloudinaryParams.desktopBanner
        : cloudinaryParams.mobileBanner;
    } else {
      return slotIdString === "desktop"
        ? cloudinaryParams.legacyDesktopBanner
        : cloudinaryParams.legacyMobileBanner;
    }
  };

  // Unused props passed from discovery panel
  export let type: string = null;
  type;

  // Create tracking entities for the banner
  const createTrackingEntities = (bannerData: Banner) => {
    const entities: any[] = [];

    // Create BannerEntity with available data
    const bannerEntity = new BannerEntity({
      image_url: bannerData.image_url || bannerData.desktop_image_url || "",
      forward_url: bannerData.forward_url || bannerData.target_url || "",
      slot_id: bannerData.slot_id || "kevel_banner",
      width: bannerData.width || 0,
      height: bannerData.height || 0,
    });
    entities.push(bannerEntity);

    // Create KevelBannerAdEntity with available data
    if (bannerData.ad_id) {
      const kevelBannerEntity = new KevelBannerAdEntity({
        object_tracking_id: bannerData.object_tracking_id,
        ad_id: bannerData.ad_id,
        advertiser_id: bannerData.advertiser_id,
        campaign_id: bannerData.campaign_id,
        creative_id: bannerData.creative_id,
        flight_id: bannerData.flight_id,
        priority_id: bannerData.priority_id,
        placement_name: bannerData.placement_name || "unknown",
        placement_position: bannerData.placement_position,
        desktop_image_url: bannerData.desktop_image_url,
        mobile_image_url: bannerData.mobile_image_url,
        events: bannerData.events,
      });
      entities.push(kevelBannerEntity);
    } else {
      const bannerAdEntity = new BannerAdEntity({
        product_line_ids: bannerData.product_line_ids || [],
        slot_id: bannerData.slot_id || "",
        campaign_id: bannerData.campaign_id,
        campaign_content_ad_type: bannerData.campaign_content_ad_type,
        campaign_content_id: bannerData.campaign_content_id,
        campaign_segment_id: bannerData.campaign_segment_id,
        contract_id: bannerData.contract_id,
        placement_id: bannerData.placement_id,
        placement_label: bannerData.placement_label,
        placement_name: bannerData.placement_name,
        placement_page_name: bannerData.placement_page_name,
        placement_value: bannerData.placement_value,
        sponsor_tags: bannerData.sponsor_tags || [],
      });
      entities.push(bannerAdEntity);
    }
    return entities;
  };

  const mobileEntities = createTrackingEntities(banner);
  const desktopEntities = createTrackingEntities(banner);

  let mobileBannerDislay, desktopBannerDislay, mobileParams, desktopParams;

  const isExternalBanner = banner.object_tracking_id || banner.ad_id;

  if (isExternalBanner) {
    mobileBannerDislay = {
      display_sponsored_label: true,
      forward_url: banner.target_url,
      image_url: `https://static.adzerk.net/Advertisers/${banner.mobile_image_url}`,
    };
    desktopBannerDislay = {
      display_sponsored_label: true,
      forward_url: banner.target_url,
      image_url: `https://static.adzerk.net/Advertisers/${banner.desktop_image_url}`,
    };
  } else {
    if (!banner.carrot_ad_format) {
      const imageParams = getImageParams(banner.width, "desktop");
      mobileParams = getImageParams(banner.width, "mobile");
      desktopParams = getImageParams(banner.width, "desktop");
    }

    mobileBannerDislay = { ...banner, image_url: getCloudinaryUrl(banner.image_url, mobileParams) };
    desktopBannerDislay = {
      ...banner,
      image_url: getCloudinaryUrl(banner.image_url, desktopParams),
    };
  }
</script>

<style>
  .extra-padding {
    padding-inline: 1.5rem;
  }
</style>

<div
  data-testid="banner"
  class="fizz-container"
  class:extra-padding={["home", "custom_content"].includes(pageName)}
>
  {#if mobileMode === true}
    <TrackingContext entities={mobileEntities}>
      <BannerDisplay banner={mobileBannerDislay} testId={"banner-mobile"} />
    </TrackingContext>
  {/if}

  {#if mobileMode === false}
    <TrackingContext entities={desktopEntities}>
      <BannerDisplay banner={desktopBannerDislay} testId={"banner-desktop"} />
    </TrackingContext>
  {/if}
</div>
